import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import styled from '@emotion/styled';
import SideBarContainer from 'Containers/SideBarContainer';
import useTranslation from 'Hooks/useTranslation';
import { useHasPermission } from 'Utils/permissions';
import { formatNewsAdminDate } from 'Utils/format-locale-dates';
import { settingsTheme } from '../theme';
import { InstructorsHeader as PageHeader, SearchBox, Pagination, DeleteConfirmDialog } from '../Instructors/components';
import { useNews, NewsRowI, NewsStatus } from './hooks/useNews';
import NewsTable from './components/NewsTable';
import NewsFormDrawer, { NewsFormLabels } from './components/NewsFormDrawer';

const PageBody = styled.div({
  flex: 1,
  padding: '34px 40px 64px',
});

const Toolbar = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 20,
  marginTop: 26,
  flexWrap: 'wrap',
});

const StateMessage = styled.div({
  marginTop: 40,
  fontFamily: settingsTheme.font,
  fontSize: 14.5,
  color: settingsTheme.color.muted,
});

/**
 * Página de gestión de noticias (detrás de la tarjeta "Noticias" del hub de
 * Configuración). La ruta y la sección ya exigen `news.read`; aquí solo se
 * gatea cada acción con su permiso (`news.create/update/delete`).
 */
export default function News() {
  const history = useHistory();
  const { t2, language } = useTranslation('Settings');
  const { news, pagination, page, goToPage, loading, error, search, setSearch, refresh, remove } = useNews();

  const canCreate = useHasPermission('news.create');
  const canEdit = useHasPermission('news.update');
  const canDelete = useHasPermission('news.delete');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<NewsRowI | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NewsRowI | null>(null);

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (id: string) => {
    setEditing(news.find(x => x.id === id) || null);
    setFormOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const result = await remove(deleteTarget.id);
    setDeleteTarget(null);
    if (result.ok) toast.success(t2('newsToastDeleted'));
    else toast.error(result.message || t2('toastError'));
  };

  const handleSaved = (mode: 'created' | 'updated') => {
    setFormOpen(false);
    refresh();
    toast.success(mode === 'created' ? t2('newsToastCreated') : t2('newsToastUpdated'));
  };

  const formLabels: NewsFormLabels = {
    title: editing ? t2('newsFormEditTitle') : t2('newsFormAddTitle'),
    subtitle: t2('newsFormSubtitle'),
    close: t2('close'),
    fieldTitle: t2('newsFieldTitle'),
    titlePlaceholder: t2('newsTitlePlaceholder'),
    fieldDescription: t2('newsFieldDescription'),
    descriptionPlaceholder: t2('newsDescriptionPlaceholder'),
    descriptionBold: t2('newsDescriptionBold'),
    descriptionItalic: t2('newsDescriptionItalic'),
    descriptionHeading1: t2('newsDescriptionHeading1'),
    descriptionHeading2: t2('newsDescriptionHeading2'),
    descriptionBulletList: t2('newsDescriptionBulletList'),
    descriptionOrderedList: t2('newsDescriptionOrderedList'),
    descriptionParagraph: t2('newsDescriptionParagraph'),
    descriptionToolbar: t2('newsDescriptionToolbar'),
    descriptionSelectionToolbar: t2('newsDescriptionSelectionToolbar'),
    descriptionSlashMenu: t2('newsDescriptionSlashMenu'),
    fieldSchedule: t2('newsFieldSchedule'),
    scheduleNow: t2('newsScheduleNow'),
    scheduleLater: t2('newsScheduleLater'),
    scheduleHint: t2('newsFieldScheduleHint'),
    fieldPublishAt: t2('newsFieldPublishAt'),
    fieldExpiresAt: t2('newsFieldExpiresAt'),
    fieldPinned: t2('newsFieldPinned'),
    pinnedYes: t2('newsPinnedYes'),
    pinnedNo: t2('newsPinnedNo'),
    fieldImage: t2('newsFieldImage'),
    imageDrop: t2('newsImageDrop'),
    imageHint: t2('newsImageHint'),
    imageRemove: t2('newsImageRemove'),
    imageTooLarge: t2('newsImageTooLarge'),
    imageInvalidType: t2('newsImageInvalidType'),
    imageUploadFailed: t2('newsImageUploadFailed'),
    genericError: t2('toastError'),
    cancel: t2('cancel'),
    save: editing ? t2('newsSaveEdit') : t2('newsSaveAdd'),
  };

  const statusLabel = (status: NewsStatus) => {
    if (status === 'scheduled') return t2('newsStatusScheduled');
    if (status === 'expired') return t2('newsStatusExpired');
    return t2('newsStatusPublished');
  };

  return (
    <SideBarContainer navBar={false}>
      <PageBody>
        <PageHeader
          breadcrumbParent={t2('title')}
          breadcrumbCurrent={t2('newsTitle')}
          onBack={() => history.push('/configuracion')}
          title={t2('newsTitle')}
          subtitle={t2('newsSubtitle')}
          addLabel={t2('addNews')}
          onAdd={openAdd}
          canAdd={canCreate}
        />

        <Toolbar>
          <SearchBox value={search} placeholder={t2('newsSearchPlaceholder')} onChange={setSearch} />
        </Toolbar>

        {error !== null && news.length === 0 && !loading ? (
          <StateMessage>{t2('newsLoadError')}</StateMessage>
        ) : loading && news.length === 0 ? (
          <StateMessage>{t2('newsLoading')}</StateMessage>
        ) : (
          <>
            <NewsTable
              rows={news}
              canEdit={canEdit}
              canDelete={canDelete}
              columnLabels={{
                title: t2('colNewsTitle'),
                status: t2('colNewsStatus'),
                pinned: t2('colNewsPinned'),
                publishAt: t2('colNewsPublishAt'),
                createdBy: t2('colNewsCreatedBy'),
              }}
              statusLabel={statusLabel}
              pinnedLabel={t2('newsPinnedLabel')}
              formatDate={iso => formatNewsAdminDate(iso, language)}
              editLabel={t2('edit')}
              deleteLabel={t2('delete')}
              emptyMessage={t2('newsEmptyMessage')}
              onEdit={openEdit}
              onDelete={id => setDeleteTarget(news.find(x => x.id === id) || null)}
            />
            {pagination && pagination.totalPages > 1 ? (
              <Pagination
                page={page}
                pageSize={pagination.pageSize}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                onChange={goToPage}
                labels={{
                  showing: t2('paginationShowing'),
                  of: t2('paginationOf'),
                  page: t2('paginationPage'),
                  prev: t2('paginationPrev'),
                  next: t2('paginationNext'),
                }}
              />
            ) : null}
          </>
        )}
      </PageBody>

      <NewsFormDrawer
        open={formOpen}
        editing={editing}
        labels={formLabels}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
        onListChanged={refresh}
      />

      <DeleteConfirmDialog
        open={deleteTarget !== null}
        title={t2('newsDeleteTitle')}
        description={t2('newsDeleteMessage', [deleteTarget?.title || ''])}
        cancelLabel={t2('deleteCancel')}
        confirmLabel={t2('deleteConfirm')}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </SideBarContainer>
  );
}
